'use client';

import { useEffect, useEffectEvent } from 'react';

import { faMicrophone, faVideo } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslations } from 'use-intl';

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
} from '@/ui';

import ProjectUsabilityCameraVideo from './project-usability-camera-video';
import {
  toCameraSelectItems,
  type CameraError,
} from './project-usability-camera.helpers';
import { useUsabilityCamera } from './project-usability-camera.hooks';

const CAMERA_ERROR_KEY = {
  denied: 'cameraErrorDenied',
  inUse: 'cameraErrorInUse',
  none: 'cameraErrorNone',
} as const satisfies Record<CameraError, string>;

interface Props {
  projectId: string;
}

export default function ProjectUsabilityCameraSetup({ projectId }: Props) {
  const t = useTranslations('Usability');

  const {
    isCameraOn,
    cameraDeviceId,
    cameraError,
    cameraStream,
    devices,
    enableCamera,
    deactivateCamera,
    switchCamera,
    restorePreference,
  } = useUsabilityCamera(projectId);

  const restoreOnMount = useEffectEvent(() => {
    void restorePreference();
  });

  useEffect(() => {
    restoreOnMount();
  }, []);

  return (
    <div
      className='flex w-full min-w-0 flex-col gap-3'
      data-test='ut-camera-setup'
    >
      <div className='relative flex aspect-video items-center justify-center overflow-hidden rounded-xl bg-gray-100'>
        {isCameraOn && cameraStream ? (
          <ProjectUsabilityCameraVideo
            stream={cameraStream}
            className='size-full -scale-x-100 object-cover'
          />
        ) : (
          <FontAwesomeIcon
            icon={faVideo}
            className='text-2xl text-gray-400'
            aria-hidden='true'
          />
        )}
      </div>

      <div className='flex items-center justify-between gap-3 rounded-lg border border-gray-200 p-3'>
        <span className='flex items-center gap-2 text-sm text-gray-900'>
          <FontAwesomeIcon
            icon={faVideo}
            className='text-base text-gray-500'
            aria-hidden='true'
          />
          {t('cameraLabel')}
        </span>
        <Switch
          checked={isCameraOn}
          onCheckedChange={(next) =>
            next ? enableCamera() : deactivateCamera()
          }
          aria-label={t('cameraLabel')}
          data-test='ut-camera-setup-toggle'
        />
      </div>

      {isCameraOn && devices.length > 0 && (
        <Select
          value={cameraDeviceId}
          onValueChange={(value) => value && switchCamera(value)}
          items={toCameraSelectItems(devices, t('cameraGenericLabel'))}
        >
          <SelectTrigger data-test='ut-camera-setup-device'>
            <SelectValue placeholder={t('cameraSelectPlaceholder')} />
          </SelectTrigger>
          <SelectContent>
            {devices.map((device) => (
              <SelectItem key={device.deviceId} value={device.deviceId}>
                {device.label || t('cameraGenericLabel')}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}

      <div className='flex items-center gap-2 rounded-lg border border-gray-200 p-3 text-sm text-gray-500'>
        <FontAwesomeIcon
          icon={faMicrophone}
          className='text-base text-gray-500'
          aria-hidden='true'
        />
        {t('micRequiredLabel')}
      </div>

      {cameraError && (
        <p className='text-xs text-red-600' data-test='ut-camera-setup-error'>
          {t(CAMERA_ERROR_KEY[cameraError])}
        </p>
      )}
    </div>
  );
}
